import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Skeleton } from '@/components/ui/skeleton';
import { taskService } from '@/app/services/taskService';
import { TaskStatus } from '@/app/types/task';
import { cn } from '@/lib/utils';

interface TaskStatusCardsWidgetProps {
  className?: string;
}

const TaskStatusCardsWidget: React.FC<TaskStatusCardsWidgetProps> = ({ className }) => {
  const navigate = useNavigate();

  const { data: openTasks, isLoading: openLoading } = useQuery({
    queryKey: ['tasks', 'status', 'open'],
    queryFn: () => taskService.list({ status: TaskStatus.OPEN, limit: 1 }),
    refetchInterval: 30000,
  });

  const { data: completedTasks, isLoading: completedLoading } = useQuery({
    queryKey: ['tasks', 'status', 'completed'],
    queryFn: () => taskService.list({ status: TaskStatus.COMPLETED, limit: 1 }),
    refetchInterval: 30000,
  });

  const { data: inProgressTasks, isLoading: inProgressLoading } = useQuery({
    queryKey: ['tasks', 'in-progress'],
    queryFn: async () => {
      const result = await taskService.list({ status: TaskStatus.OPEN, limit: 100 });
      const now = new Date();
      const yesterday = new Date(now.getTime() - 24 * 60 * 60 * 1000);
      const inProgress = result.items.filter((task) => {
        if (!task.updated_at) return false;
        return new Date(task.updated_at) >= yesterday && task.status === TaskStatus.OPEN;
      });
      return { total: inProgress.length };
    },
    refetchInterval: 30000,
  });

  const isLoading = openLoading || completedLoading || inProgressLoading;

  const openCount = openTasks?.total || 0;
  const inProgressCount = inProgressTasks?.total || 0;
  const completedCount = completedTasks?.total || 0;
  const total = openCount + inProgressCount + completedCount;

  const cards = [
    {
      label: 'Open',
      value: openCount,
      accent: 'teal' as const,
      onClick: () => navigate('/tasks?status=open'),
    },
    {
      label: 'In Progress',
      value: inProgressCount,
      accent: 'violet' as const,
      onClick: () => navigate('/tasks?status=open'),
    },
    {
      label: 'Completed',
      value: completedCount,
      accent: 'emerald' as const,
      onClick: () => navigate('/tasks?status=completed'),
    },
  ];

  const accentColors = {
    teal: { text: 'text-primary', dot: 'bg-primary', bar: 'bg-primary' },
    violet: { text: 'text-warning', dot: 'bg-warning', bar: 'bg-warning' },
    emerald: { text: 'text-success', dot: 'bg-success', bar: 'bg-success' },
  };

  // Calculate percentages for the stacked bar
  const openPct = total > 0 ? (openCount / total) * 100 : 0;
  const inProgressPct = total > 0 ? (inProgressCount / total) * 100 : 0;
  const completedPct = total > 0 ? (completedCount / total) * 100 : 0;

  if (isLoading) {
    return (
      <div className={cn("rounded-lg border bg-card", className)}>
        <div className="px-5 pt-4 pb-3">
          <Skeleton className="h-4 w-28" />
        </div>
        <div className="px-5 pb-2">
          <Skeleton className="h-1.5 w-full rounded-full" />
        </div>
        <div className="grid grid-cols-3 gap-5 px-5 pb-4 pt-2">
          {[1, 2, 3].map((i) => (
            <div key={i}>
              <Skeleton className="h-3 w-16 mb-2" />
              <Skeleton className="h-7 w-10" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className={cn("rounded-lg border bg-card", className)}>
      <div className="px-5 pt-4 pb-3 flex items-center justify-between">
        <h3 className="text-sm font-medium text-muted-foreground">Task Overview</h3>
        <span className="text-xs text-muted-foreground tabular-nums">{total} total</span>
      </div>

      {/* Stacked progress bar */}
      {total > 0 && (
        <div className="px-5 pb-3">
          <div className="flex h-1.5 w-full rounded-full overflow-hidden bg-muted">
            <div
              className="bg-primary transition-all duration-500 ease-out"
              style={{ width: `${openPct}%` }}
            />
            <div
              className="bg-warning transition-all duration-500 ease-out"
              style={{ width: `${inProgressPct}%` }}
            />
            <div
              className="bg-success transition-all duration-500 ease-out"
              style={{ width: `${completedPct}%` }}
            />
          </div>
        </div>
      )}

      <div className="grid grid-cols-3 divide-x">
        {cards.map((card) => {
          const colors = accentColors[card.accent];
          return (
            <button
              key={card.label}
              onClick={card.onClick}
              className="px-5 py-3 text-left hover:bg-muted/30 transition-colors"
            >
              <div className="flex items-center gap-1.5 mb-0.5">
                <div className={cn("h-2 w-2 rounded-full", colors.dot)} />
                <span className="text-xs text-muted-foreground">{card.label}</span>
              </div>
              <p className={cn("text-2xl font-semibold tabular-nums", colors.text)}>
                {card.value}
              </p>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default TaskStatusCardsWidget;
